// useFilters.ts
import type {
	DataGridColumnDef,
	DataGridFilterAPIOptions,
	DataGridFilterFetcherResponse,
	DataGridFilterOptionsState,
} from '@components/organisms';
import * as React from 'react';
import useApi from '../../../hooks/use-api';
import type { FilterOptionChange } from '../filter-menu';

export interface DataGridFilterOption {
	label: string;
	value: any;
}

export interface FilterOptionsState {
	hasError: boolean;
	isLoading: boolean;
	isInitialized: boolean;
	options: DataGridFilterOption[];
	retry?: () => void;
}

interface UseFiltersParams<T, CD> {
	columns: DataGridColumnDef<CD>[];
	data: T[];
	apiArgs?: any[];
	dynamicFiltersOptions: DataGridFilterFetcherResponse | null;
	handleFiltersChange: (filters: FilterOptionChange[] | FilterOptionChange, isInitial?: boolean) => void;
	isDynamicFiltersInitialized: boolean;
}

/**
 * Helper function to derive options from data.
 * If the filter configuration has a `transform` function, use it.
 */
function deriveOptionsFromData<T, CD>(column: DataGridColumnDef<CD>, data: T[]): DataGridFilterOption[] {
	if (!column.filter) return [];
	if (typeof column.filter === 'object' && column.filter.options) {
		return column.filter.options;
	}
	const values = new Set(
		data
			.map(row => {
				const value = (row as any)[column.key];
				return column.filter?.transform ? column.filter.transform(value) : value;
			})
			.filter(value => value != null)
	);
	return Array.from(values).map(value => ({
		label: String(value),
		value,
	}));
}

/**
 * Custom hook that loads filter options.
 * On mount it loads all filters. Each column’s `retry`
 * only reloads that single column.
 */
function useFilters<T, CD>({
	columns,
	data,
	apiArgs = [],
	dynamicFiltersOptions,
	handleFiltersChange,
	isDynamicFiltersInitialized,
}: UseFiltersParams<T, CD>) {
	const { fetchData, clearCache } = useApi();
	const [filterStates, setFilterStates] = React.useState<DataGridFilterOptionsState | undefined>();
	const [areFiltersInitialized, setAreFiltersInitialized] = React.useState(false);
	// Initial filter defaults must apply exactly once: re-applying on a later initColumns run
	// (e.g. when dynamic options arrive) would resurrect defaults the user already cleared.
	const initialFiltersAppliedRef = React.useRef(false);
	const mounted = React.useRef(true);
	const columnsRef = React.useRef(columns);
	columnsRef.current = columns;
	const dataRef = React.useRef(data);
	dataRef.current = data;

	const updateSingleFilter = React.useCallback((columnKey: string, newState: Partial<FilterOptionsState>) => {
		setFilterStates(prev => ({
			...prev,
			[columnKey]: {
				...(prev ?? {})[columnKey],
				...newState,
			},
		}));
	}, []);

	// Load more pages in the background
	const loadMorePages = React.useCallback(
		async (api: DataGridFilterAPIOptions, columnKey: string, currentOptions: DataGridFilterOption[]) => {
			if (!mounted.current) return;

			try {
				let currentPage = 2; // Start from page 2
				let hasMorePages = true;
				const seenValues = new Set(
					currentOptions.map(opt => (typeof opt.value === 'object' ? JSON.stringify(opt.value) : String(opt.value)))
				);

				while (hasMorePages && mounted.current) {
					const result = await fetchData({
						args: apiArgs,
						method: api.method,
						params: {
							...api.params,
							page: currentPage,
							itemsPerPage: 30,
						},
						overrideCacheKey: `${columnKey}-${currentPage}`,
					});

					if (!result || !mounted.current) break;

					const pageOptions = api.responseParser ? api.responseParser(result) : result;

					// Deduplicate options
					const newOptions = pageOptions.filter((option: DataGridFilterOption) => {
						const key = typeof option.value === 'object' ? JSON.stringify(option.value) : String(option.value);

						if (seenValues.has(key)) return false;
						seenValues.add(key);
						return true;
					});

					if (newOptions.length > 0) {
						setFilterStates(prev => {
							const prevState = prev ?? {};
							const prevColumnState = prevState[columnKey] ?? {};
							return {
								...prevState,
								[columnKey]: {
									...prevColumnState,
									options: [...(prevColumnState.options ?? []), ...newOptions],
								} as FilterOptionsState,
							};
						});
					}

					if (result.meta) {
						hasMorePages = currentPage < result.meta.totalPages;
						currentPage++;
					} else {
						hasMorePages = false;
					}
				}
			} catch (error) {
				console.error('Error loading additional pages for column', columnKey, error);
			}
		},
		[...apiArgs, fetchData]
	);

	const reloadColumn = React.useCallback(
		async (column: DataGridColumnDef<CD>, initialLoad = false) => {
			// Case 1: explicit options
			if (column.filter?.options) {
				setFilterStates(prev => ({
					...prev,
					[column.key]: {
						hasError: false,
						isLoading: false,
						isInitialized: true,
						options: column.filter?.options || [],
						retry: () => {
							reloadColumn(column);
						},
					},
				}));
				return;
			}

			// Case 2: API
			if (column.filter?.api) {
				// If initialLoad is true, we don't need to fetch the filter options
				if (initialLoad) {
					setFilterStates(prev => ({
						...prev,
						[column.key]: {
							hasError: false,
							isLoading: false,
							isInitialized: false,
							options: [],
							retry: () => {
								reloadColumn(column);
							},
						},
					}));
					return;
				}

				try {
					const api = column.filter.api;

					// First load just page 1 for initial render
					const firstPageResult = await fetchData({
						args: apiArgs,
						method: api.method,
						params: {
							...api.params,
							page: 1,
							itemsPerPage: 30,
						},
						overrideCacheKey: `${column.key}-${1}`,
					});

					if (!mounted.current) return;

					const firstPageOptions = api.responseParser ? api.responseParser(firstPageResult) : firstPageResult;

					setFilterStates(prev => ({
						...prev,
						[column.key]: {
							hasError: false,
							isLoading: false,
							isInitialized: true,
							options: firstPageOptions,
							retry: () => {
								reloadColumn(column);
							},
						},
					}));

					// Load additional pages in the background
					if (firstPageResult.meta?.totalPages > 1) {
						loadMorePages(api, column.key, firstPageOptions);
					}
				} catch (error) {
					if (!mounted.current) return;

					setFilterStates(prev => ({
						...prev,
						[column.key]: {
							hasError: true,
							isLoading: false,
							isInitialized: true,
							options: [],
							retry: () => {
								reloadColumn(column);
							},
						},
					}));
				}
				return;
			}

			// Case 3: Derive from data
			const options = deriveOptionsFromData(column, dataRef.current);
			setFilterStates(prev => ({
				...prev,
				[column.key]: {
					hasError: false,
					isLoading: false,
					isInitialized: true,
					options,
					retry: () => {
						reloadColumn(column);
					},
				},
			}));
		},
		[...apiArgs, fetchData, loadMorePages]
	);

	const initColumns = React.useCallback(() => {
		const filterableColumnKeys = new Set<string>();
		const initialFilterUpdates: FilterOptionChange[] = [];

		columnsRef.current.forEach(column => {
			const dynamicFilterOptions = dynamicFiltersOptions?.[column.key];
			if (column.filter) {
				filterableColumnKeys.add(column.key);
				if (dynamicFilterOptions) {
					column.filter.options = dynamicFilterOptions;
				}
				// Defaults whose options are static column config apply immediately; only defaults that
				// depend on fetched options wait for the dynamic filters to initialize.
				if (
					column.filter.initial &&
					!initialFiltersAppliedRef.current &&
					(column.filter.options?.length || isDynamicFiltersInitialized)
				) {
					const updates: FilterOptionChange[] = column.filter.initial.flatMap(value => {
						const option = column.filter?.options?.find(option => option.value.toString() === value.toString());
						if (option) {
							return [
								{
									groupKey: column.key,
									optionKey: String(option.value),
									label: option.label,
									isSelected: true,
								},
							];
						}
						return [];
					});
					initialFilterUpdates.push(...updates);
				}
				reloadColumn(column, true);
			}
		});

		// apply all initial filters in a single update; an empty update with isInitial=true
		// would reset active filters and wipe defaults applied on a previous run
		if (initialFilterUpdates.length > 0) {
			initialFiltersAppliedRef.current = true;
			handleFiltersChange(initialFilterUpdates, true);
		}

		// load options for filters without a matching filterable column to setFilterStates
		if (dynamicFiltersOptions) {
			const hiddenFilters: Record<string, FilterOptionsState> = {};
			for (const columnKey in dynamicFiltersOptions) {
				if (filterableColumnKeys.has(columnKey)) {
					continue;
				}
				const options = dynamicFiltersOptions[columnKey];

				hiddenFilters[columnKey] = {
					hasError: false,
					isLoading: false,
					isInitialized: true,
					options,
				};
			}

			setFilterStates(prev => ({
				...prev,
				...hiddenFilters,
			}));
		}
		setAreFiltersInitialized(true);
	}, [reloadColumn, dynamicFiltersOptions, isDynamicFiltersInitialized]);

	const refreshColumns = React.useCallback(() => {
		clearCache();
		initColumns();
	}, [clearCache, initColumns]);

	React.useEffect(() => {
		initColumns();
	}, [initColumns]);

	React.useEffect(() => {
		return () => {
			mounted.current = false;
		};
	}, []);

	return {
		filterStates,
		reloadColumn,
		updateSingleFilter,
		refreshColumns,
		areFiltersInitialized,
	};
}

export default useFilters;
